import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { AnimatePresence } from 'framer-motion';
import { url } from '../../../../../env';
import GlobalScoreHeader from './GlobalScoreHeader';
import AnalysisCard from './AnalysisCard';
import DiscrepanciaModal from './DiscrepanciaModal';
import RejectModal from './RejectModal';
import ReanalyzeConfirmModal from './ReanalyzeConfirmModal';
import ReplacementUploadSlot from './ReplacementUploadSlot';

const ValidacionIAView = ({ categories = {}, persona, onVerDocumento, onRefreshData, onAnalysisDirty }) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [expandedIds, setExpandedIds] = useState({});

    // Review workflow modals
    const [rejectModalItem, setRejectModalItem] = useState(null);
    const [reanalyzeModalItem, setReanalyzeModalItem] = useState(null);

    // Track items with local review_status overrides (for instant UI feedback)
    const [localReviewStatus, setLocalReviewStatus] = useState({});

    // Track items currently being reanalyzed: { [itemId]: { analysisItemId, newFileKey? } }
    const [reanalyzingItems, setReanalyzingItems] = useState({});
    // Store updated analysis data from poll results: { [itemId]: newItemData }
    const [updatedAnalysis, setUpdatedAnalysis] = useState({});
    const pollIntervalRef = useRef(null);

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

    // Polling for reanalyzing items
    useEffect(() => {
        const activeIds = Object.keys(reanalyzingItems);
        if (activeIds.length === 0) {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            return;
        }

        const poll = async () => {
            const currentToken = localStorage.getItem('token');
            const h = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` };

            for (const itemId of Object.keys(reanalyzingItems)) {
                const { analysisItemId, newFileKey } = reanalyzingItems[itemId];

                // URL depends on whether we have an analysisItemId or we are tracking a raw file retry
                let pollUrl;
                if (analysisItemId) {
                    pollUrl = `${url}/document-analysis/items/${analysisItemId}/reanalysis-status`
                        + (newFileKey ? `?file_key=${encodeURIComponent(newFileKey)}` : '');
                } else if (newFileKey && persona?.id) {
                    pollUrl = `${url}/document-analysis/status-by-file?persona_id=${persona.id}&file_key=${encodeURIComponent(newFileKey)}`;
                } else {
                    continue; // Missing required data to poll
                }

                try {
                    const res = await axios.get(pollUrl, { headers: h });
                    if (res.data.success && res.data.status === 'completed') {
                        const newItemData = res.data.new_item || res.data.item;

                        // Store the new analysis data locally
                        if (newItemData) {
                            setUpdatedAnalysis(prev => ({
                                ...prev,
                                [itemId]: newItemData,
                            }));
                        }

                        // Remove from reanalyzing
                        setReanalyzingItems(prev => {
                            const next = { ...prev };
                            delete next[itemId];
                            return next;
                        });
                        // Notify parent refresh hooks
                        if (onRefreshData) onRefreshData();
                        onAnalysisDirty?.();
                    }
                } catch (err) {
                    console.error('Poll error for item', analysisItemId, err);
                }
            }
        };

        pollIntervalRef.current = setInterval(poll, 10000);
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [reanalyzingItems, onRefreshData, onAnalysisDirty]);

    // Convert grouped categories into flat items array
    const items = useMemo(() => {
        let allItems = [];
        Object.entries(categories || {}).forEach(([categoryName, files]) => {
            if (categoryName === 'REFERENCES') return;

            if (Array.isArray(files)) {
                files.forEach((file, index) => {
                    // Use a truly unique ID consistently: category + (DB ID or index)
                    const itemId = file.id
                        ? `${categoryName}-${file.id}`
                        : `${categoryName}-${file.name}-${index}`;

                    // Use poll-returned data if available, otherwise original
                    const freshData = updatedAnalysis[itemId];
                    const analysisData = freshData || file.ai_analysis || file.analysis || file.extracted_data || {};
                    const overriddenStatus = localReviewStatus[itemId];

                    const createdAt = file.created_at || null;
                    const hasAnalysis = !!(analysisData.id || analysisData.status || analysisData.finding);

                    // Timeout detection: file uploaded >5 min ago with no AI analysis
                    let isFailedByTimeout = false;
                    if (!hasAnalysis && createdAt) {
                        const uploadDate = new Date(createdAt);
                        isFailedByTimeout = (Date.now() - uploadDate.getTime()) > (5 * 60 * 1000);
                    }

                    const computedStatus = analysisData.status === 'error' || isFailedByTimeout
                        ? 'error'
                        : (hasAnalysis ? (analysisData.status || file.status || 'warning') : 'warning');

                    // Accuracy: use real data, or 0 if no analysis yet
                    const computedAccuracy = hasAnalysis
                        ? Number(analysisData.accuracy !== undefined && analysisData.accuracy !== null ? analysisData.accuracy : (file.accuracy ?? 0))
                        : 0;

                    // Finding: error message, real findings, or pending message
                    let computedFinding;
                    if (computedStatus === 'error' && !hasAnalysis) {
                        computedFinding = [{
                            message: isFailedByTimeout
                                ? 'El análisis de IA excedió el tiempo límite. Elimine el documento y vuelva a subirlo.'
                                : 'El análisis de este documento falló. Elimínelo y vuelva a subirlo.', is_valid: false
                        }];
                    } else if (hasAnalysis) {
                        computedFinding = analysisData.finding || file.finding || [];
                    } else {
                        computedFinding = [{ message: 'Analizando documento con Inteligencia Artificial...', is_valid: false }];
                    }

                    // Format upload date for display
                    const displayDate = createdAt
                        ? new Date(createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : null;

                    allItems.push({
                        id: itemId,
                        category: categoryName,
                        fileName: freshData?.file_name || file.name,
                        status: computedStatus,
                        accuracy: computedAccuracy,
                        created_at: displayDate || 'Sin fecha',
                        url: freshData?.signed_url || file.signed_url || file.url || file.file_path || '#',
                        finding: computedFinding,
                        original_data: { ...file, ai_analysis: freshData || file.ai_analysis },
                        // Review fields
                        review_status: overriddenStatus || analysisData.review_status || 'pending',
                        review_tags: analysisData.review_tags || null,
                        review_reason: analysisData.review_reason || null,
                        analysisItemId: analysisData.id || null,
                    });
                });
            }
        });

        // Dedup: si el JSON de UserFile tiene paths duplicados que mapean al mismo
        // DocumentAnalysisItem.id, evitar renderizar múltiples cards idénticas.
        const seenAnalysisIds = new Set();
        allItems = allItems.filter(item => {
            if (!item.analysisItemId) return true;
            if (seenAnalysisIds.has(item.analysisItemId)) return false;
            seenAnalysisIds.add(item.analysisItemId);
            return true;
        });

        const isPM = persona && (persona.selectedTypePerson === 2 || persona.tipo_persona === 'PM' || persona.typePerson === 2 || persona.company);

        allItems.sort((a, b) => {
            const aIsAval = a.category?.toUpperCase().includes('AVAL') || (isPM && a.category?.toUpperCase().includes('IDENTIFICACIONES'));
            const bIsAval = b.category?.toUpperCase().includes('AVAL') || (isPM && b.category?.toUpperCase().includes('IDENTIFICACIONES'));

            if (aIsAval && !bIsAval) return 1;
            if (!aIsAval && bIsAval) return -1;
            return 0;
        });

        return allItems;
    }, [categories, localReviewStatus, updatedAnalysis, persona]);

    const overallAccuracy = useMemo(() => {
        if (!items || items.length === 0) return 0;
        const total = items.reduce((acc, curr) => {
            const val = Number(curr.accuracy);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);
        return Math.round(total / items.length);
    }, [items]);

    // Compute review counters from displayed items (always consistent with UI)
    const computedReviewStats = useMemo(() => {
        const total = items.length;
        const approved = items.filter(i => i.review_status === 'approved').length;
        const rejected = items.filter(i => i.review_status === 'rejected').length;
        const pending = items.filter(i => i.review_status === 'pending').length;
        return { total, approved, rejected, pending };
    }, [items]);

    const allExpanded = useMemo(() => {
        if (items.length === 0) return false;
        return items.every(item => expandedIds[item.id]);
    }, [items, expandedIds]);

    const handleToggleExpand = useCallback((id) => {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const handleToggleAll = useCallback(() => {
        if (allExpanded) {
            setExpandedIds({});
        } else {
            const newExpanded = {};
            items.forEach(item => { newExpanded[item.id] = true; });
            setExpandedIds(newExpanded);
        }
    }, [allExpanded, items]);


    // ── Actions ──

    const handleApproveItem = useCallback(async (item) => {
        if (!item.analysisItemId) return;
        try {
            const res = await axios.post(
                `${url}/document-analysis/items/${item.analysisItemId}/approve`,
                {},
                { headers }
            );
            if (res.data.success) {
                setLocalReviewStatus(prev => ({ ...prev, [item.id]: 'approved' }));

            }
        } catch (err) {
            console.error('Error approving item:', err);
        }
    }, [headers]);

    const handleRejectItem = useCallback((item) => {
        setRejectModalItem(item);
    }, []);

    const handleConfirmReject = useCallback(async (item, { tags, reason }) => {
        if (!item.analysisItemId) {
            console.error('[Reject] No analysisItemId, aborting');
            return;
        }
        try {
            const res = await axios.post(
                `${url}/document-analysis/items/${item.analysisItemId}/reject`,
                { tags, reason },
                { headers }
            );
            if (res.data.success) {
                setLocalReviewStatus(prev => ({ ...prev, [item.id]: 'rejected' }));
                onAnalysisDirty?.();
            }
        } catch (err) {
            console.error('[Reject] error:', err.response?.data || err.message);
        }
    }, [headers, onAnalysisDirty]);

    const handleCancelReject = useCallback(async (item) => {
        if (!item.analysisItemId) return;
        const res = await axios.post(
            `${url}/document-analysis/items/${item.analysisItemId}/cancel-reject`,
            {},
            { headers }
        );
        if (res.data.success) {
            setLocalReviewStatus(prev => ({ ...prev, [item.id]: 'pending' }));
            onAnalysisDirty?.();
        }
    }, [headers, onAnalysisDirty]);

    const handleReanalyzeItem = useCallback((item) => {
        setReanalyzeModalItem(item);
    }, []);

    const handleConfirmReanalyze = useCallback(async (item) => {
        if (!item.analysisItemId) return;
        const res = await axios.post(
            `${url}/document-analysis/items/${item.analysisItemId}/reanalyze`,
            {},
            { headers }
        );
        if (res.data.success) {
            setLocalReviewStatus(prev => ({ ...prev, [item.id]: 'pending' }));
            setReanalyzingItems(prev => ({ ...prev, [item.id]: { analysisItemId: item.analysisItemId } }));
            onAnalysisDirty?.();
        }
    }, [headers, onAnalysisDirty]);

    const handleReplacementComplete = useCallback((item, { new_file_key }) => {
        // Switch item back to pending so it renders as AnalysisCard with shimmer
        setLocalReviewStatus(prev => ({ ...prev, [item.id]: 'pending' }));
        // Mark as reanalyzing to show shimmer overlay + start polling
        setReanalyzingItems(prev => ({
            ...prev,
            [item.id]: { analysisItemId: item.analysisItemId, newFileKey: new_file_key },
        }));
        onAnalysisDirty?.();
    }, [onAnalysisDirty]);

    const handleRetryAnalysis = useCallback(async (item) => {
        if (!persona?.id) return;
        const fileKey = item.original_data?.key || item.original_data?.full_path;
        if (!fileKey) {
            console.error('[RetryAnalysis] No file_key found for item:', item.id);
            return;
        }
        try {
            const res = await axios.post(
                `${url}/document-analysis/retry`,
                { persona_id: persona.id, mode: 'single_file', file_key: fileKey },
                { headers }
            );
            if (res.data.success) {
                // Mark as reanalyzing so shimmer shows + polling starts
                setReanalyzingItems(prev => ({
                    ...prev,
                    [item.id]: { analysisItemId: null, newFileKey: fileKey },
                }));
            }
        } catch (err) {
            console.error('[RetryAnalysis] error:', err.response?.data || err.message);
        }
    }, [headers, persona]);

    const handleApproveAll = useCallback(async () => {
        if (!persona?.id) return;
        try {
            const res = await axios.post(
                `${url}/document-analysis/approve-all`,
                { persona_id: persona.id, min_accuracy: 90 },
                { headers }
            );
            if (res.data.success) {
                // Update local statuses for affected items
                const newStatuses = {};
                items.forEach(item => {
                    if (item.review_status === 'pending' && item.accuracy >= 90) {
                        newStatuses[item.id] = 'approved';
                    }
                });
                setLocalReviewStatus(prev => ({ ...prev, ...newStatuses }));

            }
        } catch (err) {
            console.error('Error approving all:', err);
        }
    }, [persona, items, headers]);

    const handleVerDiscrepancia = (item) => {
        setSelectedItem(item);
        setIsModalOpen(true);
    };

    return (
        <main style={{
            flex: 1, overflowY: 'auto', backgroundColor: '#f8fafc',
            padding: '1rem', position: 'relative'
        }}>
            <div style={{
                maxWidth: '80rem', margin: '0 auto', display: 'flex',
                flexDirection: 'column', gap: '2rem'
            }}>

                <GlobalScoreHeader
                    overallAccuracy={overallAccuracy}
                    onApproveAll={handleApproveAll}
                    allExpanded={allExpanded}
                    onToggleAll={handleToggleAll}
                    reviewStats={computedReviewStats}
                />

                {items.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '1rem', border: '1px dashed #cbd5e1' }}>
                        <i className="pi pi-folder-open" style={{ fontSize: '3rem', color: '#94a3b8', marginBottom: '1rem' }}></i>
                        <h3 style={{ color: '#475569', fontWeight: 'bold' }}>No hay documentos</h3>
                        <p style={{ color: '#94a3b8' }}>Este usuario no cuenta con documentos subidos para analizar.</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '1.5rem'
                    }}>
                        <AnimatePresence mode="popLayout">
                            {items.map((item) => (
                                item.review_status === 'rejected' ? (
                                    <ReplacementUploadSlot
                                        key={item.id + '-slot'}
                                        item={item}
                                        onCancelReject={handleCancelReject}
                                        onReplacementComplete={handleReplacementComplete}
                                    />
                                ) : (
                                    <AnalysisCard
                                        key={item.id}
                                        item={item}
                                        persona={persona}
                                        isExpanded={!!expandedIds[item.id]}
                                        isReanalyzing={!!reanalyzingItems[item.id]}
                                        onToggleExpand={handleToggleExpand}
                                        onAprobar={handleApproveItem}
                                        onRechazar={handleRejectItem}
                                        onReanalizar={handleReanalyzeItem}
                                        onVerDiscrepancia={handleVerDiscrepancia}
                                        onVerDocumento={onVerDocumento}
                                        onRetryAnalysis={handleRetryAnalysis}
                                    />
                                )
                            ))}
                        </AnimatePresence>
                    </div>
                )}

            </div>

            <DiscrepanciaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                item={selectedItem}
                persona={persona}
                onSaved={() => onAnalysisDirty?.()}
            />

            <RejectModal
                isOpen={!!rejectModalItem}
                onClose={() => setRejectModalItem(null)}
                item={rejectModalItem}
                onConfirmReject={handleConfirmReject}
            />

            <ReanalyzeConfirmModal
                isOpen={!!reanalyzeModalItem}
                onClose={() => setReanalyzeModalItem(null)}
                item={reanalyzeModalItem}
                onConfirmReanalyze={handleConfirmReanalyze}
            />
        </main>
    );
};

export default ValidacionIAView;
